// mediaComment.js
//
// TikTok-style comment sheet for a Moment (media) post: slides up from the
// bottom over the lower 68% of the screen while Moment.js shrinks the video
// into the space above. Tap above the sheet, the X, or press Esc to close.
//
// Mirrors CommentCard.js behaviour:
//   - ONE comment icon per comment (top-level AND nested). There is no
//     separate "Reply" icon — tapping the comment icon opens an inline
//     reply box right under that comment, whatever its depth.
//   - Replies are nested via parentCommentId and can be shown / hidden.
//   - Support icon: "send support" on other users' comments, "view
//     earnings" on your own (only when the matching callbacks are given).
//   - Views are tracked automatically when a comment is rendered.
//
// Uses the MEDIA post flow (mediaSocket), not the feed flow:
//   onAddComment({ postId, text, comment, parentCommentId })
//       -> mediaSocket.mediaAddComment
//   onLikeComment({ commentId, postId, chatId })
//       -> mediaSocket.mediaLikeComment
//   comments come from mediaCommentsByPost[postId] (kept live by
//   mediaOnNewComment / mediaOnCommentCountUpdate / mediaOnCommentLikeUpdate)
//
// Usage:
//   <MediaComment
//     post={post}
//     comments={mediaCommentsByPost[post.postId] || []}
//     myChatId={myChatId}
//     users={users}
//     currentUser={currentUser}
//     avatarUrl={avatarUrl}
//     onBack={() => setShowCommentSheet(false)}
//     onAddComment={onAddMediaComment}
//     onLikeComment={onLikeMediaComment}
//   />

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HeartIcon,
  CommentIcon,
  EyeIcon,
  SupportIcon,
  ShareIcon,
} from "../components/mediaIcon";

/* =========================================================
   ICONS
========================================================= */

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

/* =========================================================
   HELPERS
========================================================= */

const getCommentId = (comment) =>
  comment?.commentId || comment?._id || comment?.id;

const getCommentText = (comment) => comment?.text || comment?.comment || "";

const getAuthorChatId = (comment) =>
  comment?.author?.chatId || comment?.chatId || comment?.user?.chatId;

const getAuthorName = (comment) =>
  comment?.author?.displayName ||
  comment?.author?.name ||
  comment?.author?.username ||
  comment?.user?.name ||
  comment?.user?.username ||
  comment?.authorUsername ||
  comment?.username ||
  "User";

const getAuthorUsername = (comment) => {
  const username =
    comment?.author?.username ||
    comment?.user?.username ||
    comment?.authorUsername ||
    comment?.username;

  if (!username) return "";
  return String(username).replace(/^@/, "");
};

const getCommentMedia = (comment) => {
  if (Array.isArray(comment?.media)) return comment.media.filter((m) => m?.url);
  if (comment?.media?.url) return [comment.media];
  return [];
};

const isVideo = (m) =>
  String(m?.type || "").startsWith("video") ||
  String(m?.mimeType || "").startsWith("video");

const fmt = (n) =>
  !n ? null : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

const formatTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diff = Math.max(0, Date.now() - date.getTime());
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Flat comment list -> tree (children nested under parentCommentId).
// A comment whose parent isn't in the list is shown at top level so it
// never disappears.
const buildCommentTree = (flat) => {
  const sorted = [...flat].sort(
    (a, b) =>
      new Date(a?.createdAt || a?.created_at || 0).getTime() -
      new Date(b?.createdAt || b?.created_at || 0).getTime()
  );

  const byId = new Map();
  sorted.forEach((c) => byId.set(String(getCommentId(c)), { ...c, children: [] }));

  const roots = [];
  byId.forEach((node) => {
    const parentId = node.parentCommentId ? String(node.parentCommentId) : null;
    const parent = parentId ? byId.get(parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  return roots;
};

/* =========================================================
   COMMENT CARD (private — not exported)
========================================================= */

function MediaCommentCard({
  item,
  postId,
  myChatId,
  resolveUserAvatar,
  replyingCommentId,
  setReplyingCommentId,
  replyText,
  setReplyText,
  isSubmitting,
  onSubmitReply,
  autoExpandIds,
  onLikeComment,
  onSupportComment,
  onViewCommentEarnings,
  onTrackCommentView,
  onShareComment,
  onOpenUser,
}) {
  const commentId = getCommentId(item);
  const authorChatId = getAuthorChatId(item);
  const authorName = getAuthorName(item);
  const username = getAuthorUsername(item);
  const text = getCommentText(item);
  const media = getCommentMedia(item);
  const avatar = resolveUserAvatar?.(authorChatId) || item?.author?.avatarUrl || "";

  const isCommentAuthor =
    myChatId && String(authorChatId) === String(myChatId);

  const hasChildren = item.children?.length > 0;
  const childrenCount = item.children?.length || 0;

  const [expanded, setExpanded] = useState(
    Boolean(autoExpandIds?.has(String(commentId)))
  );
  const [liked, setLiked] = useState(Boolean(item?.likedByMe));
  const replyRef = useRef(null);
  const viewedRef = useRef(false);

  const isReplying = String(replyingCommentId) === String(commentId);

  useEffect(() => {
    if (autoExpandIds?.has(String(commentId))) setExpanded(true);
  }, [autoExpandIds, commentId]);

  useEffect(() => {
    setLiked(Boolean(item?.likedByMe));
  }, [item?.likedByMe]);

  useEffect(() => {
    if (isReplying) setTimeout(() => replyRef.current?.focus(), 0);
  }, [isReplying]);

  // auto view tracking (once per comment), like CommentCard
  useEffect(() => {
    if (!commentId || viewedRef.current) return;
    viewedRef.current = true;
    onTrackCommentView?.({ commentId, postId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentId]);

  const handleLike = () => {
    setLiked((v) => !v);
    onLikeComment?.({ commentId, postId, chatId: myChatId });
  };

  const handleSupport = () => {
    if (isCommentAuthor) onViewCommentEarnings?.({ comment: item });
    else onSupportComment?.({ comment: item });
  };

  const showSupport =
    (isCommentAuthor && onViewCommentEarnings) ||
    (!isCommentAuthor && onSupportComment && myChatId);

  const submitReply = () => {
    if (!replyText.trim() || isSubmitting) return;
    onSubmitReply(commentId);
  };

  return (
    <div style={styles.commentWrapper}>
      <div style={styles.commentRow}>
        <div
          style={{ ...styles.avatar, cursor: onOpenUser ? "pointer" : "default" }}
          onClick={() => onOpenUser?.(authorChatId)}
        >
          {avatar ? (
            <img src={avatar} alt="" style={styles.avatarImage} />
          ) : (
            <span style={styles.avatarFallback}>
              {authorName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div style={styles.commentBody}>
          <div style={styles.commentBubble}>
            <div style={styles.authorRow}>
              <span style={styles.authorName}>{authorName}</span>
              {username ? (
                <span style={styles.username}>@{username}</span>
              ) : null}
            </div>

            {text ? <div style={styles.commentText}>{text}</div> : null}

            {media.length > 0 ? (
              <div style={styles.commentMediaWrap}>
                {media.map((m, i) => (
                  <div key={m.url || i} style={styles.commentMedia}>
                    {isVideo(m) ? (
                      <video
                        src={m.url}
                        controls
                        playsInline
                        style={styles.commentMediaImage}
                      />
                    ) : (
                      <img src={m.url} alt="" style={styles.commentMediaImage} />
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div style={styles.metaRow}>
            <span style={styles.time}>
              {formatTime(item?.createdAt || item?.created_at)}
            </span>
          </div>

          <div style={styles.actionRow}>
            {/* COMMENT — the only way to reply, at any depth */}
            <button
              type="button"
              style={{
                ...styles.actionButton,
                ...(isReplying ? styles.activeComment : {}),
              }}
              onClick={() => setReplyingCommentId(commentId)}
              aria-label="Comment"
              title="Comment"
            >
              <CommentIcon size={18} color="currentColor" />
              {fmt(item?.commentsCount ?? childrenCount) ? (
                <span style={styles.actionCount}>
                  {fmt(item?.commentsCount ?? childrenCount)}
                </span>
              ) : null}
            </button>

            {/* LIKE */}
            <button
              type="button"
              style={{
                ...styles.actionButton,
                ...(liked ? styles.activeLike : {}),
              }}
              onClick={handleLike}
              aria-label="Like"
              title="Like"
            >
              <HeartIcon size={18} color="currentColor" />
              {fmt(item?.likesCount) ? (
                <span style={styles.actionCount}>{fmt(item.likesCount)}</span>
              ) : null}
            </button>

            {/* VIEWS (display only — tracked automatically) */}
            <div style={styles.viewStat} aria-label={`${item?.viewsCount || 0} views`}>
              <EyeIcon size={18} color="currentColor" />
              {fmt(item?.viewsCount) ? (
                <span style={styles.actionCount}>{fmt(item.viewsCount)}</span>
              ) : null}
            </div>

            {/* SUPPORT / EARNINGS */}
            {showSupport ? (
              <button
                type="button"
                style={styles.actionButton}
                onClick={handleSupport}
                aria-label={isCommentAuthor ? "View earnings" : "Support"}
                title={isCommentAuthor ? "View earnings" : "Support"}
              >
                <SupportIcon size={18} color="currentColor" />
                {fmt(item?.supportsCount) ? (
                  <span style={styles.actionCount}>{fmt(item.supportsCount)}</span>
                ) : null}
              </button>
            ) : null}

            {/* SHARE */}
            <button
              type="button"
              style={styles.actionButton}
              onClick={() => onShareComment?.(item)}
              aria-label="Share"
              title="Share"
            >
              <ShareIcon size={18} color="currentColor" />
            </button>
          </div>

          {/* INLINE REPLY BOX */}
          {isReplying ? (
            <div style={styles.inlineReply}>
              <div style={styles.inlineReplyLabel}>
                Replying to <strong>@{username || authorName}</strong>
              </div>

              <textarea
                ref={replyRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitReply();
                  }
                }}
                placeholder={`Reply to @${username || authorName}`}
                rows={2}
                disabled={isSubmitting}
                style={styles.inlineReplyInput}
              />

              <div style={styles.inlineReplyActions}>
                <button
                  type="button"
                  onClick={() => setReplyingCommentId(null)}
                  disabled={isSubmitting}
                  style={styles.inlineCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReply}
                  disabled={!replyText.trim() || isSubmitting}
                  style={{
                    ...styles.inlineSend,
                    opacity: !replyText.trim() || isSubmitting ? 0.5 : 1,
                  }}
                >
                  {isSubmitting ? "Sending..." : "Reply"}
                </button>
              </div>
            </div>
          ) : null}

          {/* SHOW / HIDE REPLIES */}
          {hasChildren ? (
            <button
              type="button"
              style={styles.replyToggle}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Hide replies" : `Show replies (${childrenCount})`}
            </button>
          ) : null}

          {/* NESTED COMMENTS */}
          {expanded && hasChildren ? (
            <div style={styles.replies}>
              {item.children.map((child) => (
                <MediaCommentCard
                  key={getCommentId(child)}
                  item={child}
                  postId={postId}
                  myChatId={myChatId}
                  resolveUserAvatar={resolveUserAvatar}
                  replyingCommentId={replyingCommentId}
                  setReplyingCommentId={setReplyingCommentId}
                  replyText={replyText}
                  setReplyText={setReplyText}
                  isSubmitting={isSubmitting}
                  onSubmitReply={onSubmitReply}
                  autoExpandIds={autoExpandIds}
                  onLikeComment={onLikeComment}
                  onSupportComment={onSupportComment}
                  onViewCommentEarnings={onViewCommentEarnings}
                  onTrackCommentView={onTrackCommentView}
                  onShareComment={onShareComment}
                  onOpenUser={onOpenUser}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   MEDIA COMMENT PAGE
========================================================= */

export default function MediaComment({
  post,
  comments = [],
  myChatId,
  users,
  currentUser,
  avatarUrl,
  onBack,
  onAddComment,
  onLikeComment,
  onSupportComment,
  onViewCommentEarnings,
  onTrackCommentView,
  onShareComment,
  onOpenUser,
  style,
}) {
  const [commentText, setCommentText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [autoExpandIds, setAutoExpandIds] = useState(() => new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const postId = post?.postId || post?._id || post?.id;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onBack?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const resolveUserAvatar = (chatId) => {
    if (!chatId) return "";
    const list = Array.isArray(users) ? users : [];
    const u = list.find((x) => String(x?.chatId) === String(chatId));
    return u?.profileImageUrl || u?.avatarUrl || u?.avatar || "";
  };
  const postComments = useMemo(() => {
    const source =
      Array.isArray(comments) && comments.length
        ? comments
        : Array.isArray(post?.comments)
        ? post.comments
        : [];

    const seen = new Set();

    return source.filter((c) => {
      if (c?.postId && postId && String(c.postId) !== String(postId)) return false;

      const id = String(getCommentId(c));
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [comments, post?.comments, postId]);
  const tree = useMemo(() => buildCommentTree(postComments), [postComments]);

  const closeReply = () => {
    setReplyingCommentId(null);
    setReplyText("");
  };

  const openReply = (commentId) => {
    // switching to another comment starts a fresh reply box
    if (String(commentId) !== String(replyingCommentId)) setReplyText("");
    setReplyingCommentId(commentId);
  };

  const submit = async ({ text, parentCommentId }) => {
    if (!postId || !text || isSubmitting) return false;

    setIsSubmitting(true);

    try {
      await onAddComment?.({
        postId,
        text,
        comment: text,
        parentCommentId: parentCommentId || null,
      });
      return true;
    } catch (error) {
      console.error("Failed to add media comment:", error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTopLevel = async () => {
    const text = commentText.trim();
    if (await submit({ text })) setCommentText("");
  };

  const handleSubmitReply = async (parentCommentId) => {
    const text = replyText.trim();

    if (await submit({ text, parentCommentId })) {
      setAutoExpandIds((prev) => new Set(prev).add(String(parentCommentId)));
      closeReply();
    }
  };

  const handleShareComment = async (comment) => {
    if (onShareComment) {
      await onShareComment(comment);
      return;
    }

    const text = getCommentText(comment);
    const author = getAuthorName(comment);
    const shareText = text ? `${author}: ${text}` : `Comment by ${author}`;

    try {
      if (navigator?.share) {
        await navigator.share({ title: "Comment", text: shareText });
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Failed to share comment:", error);
      }
    }
  };

  const totalCount = postComments.length;

  return (
    <div style={{ ...styles.overlay, ...style }} role="dialog" aria-label="Comments">
      <style>{`
        @keyframes mcSheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes mcFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* dimmed area over the video — tap to close */}
      <div style={styles.backdrop} onClick={onBack} />

      <div style={styles.sheet}>
        <div style={styles.grabber} />

        {/* HEADER */}
        <header style={styles.header}>
          <div style={styles.headerSpacer} />

          <div style={styles.headerTitle}>
            <strong>
              {totalCount} {totalCount === 1 ? "comment" : "comments"}
            </strong>
          </div>

          <button
            type="button"
            onClick={onBack}
            style={styles.closeButton}
            aria-label="Close comments"
            title="Close"
          >
            <CloseIcon />
          </button>
        </header>

      {/* COMMENTS */}
      <main style={styles.commentList}>
        {tree.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <CommentIcon size={24} color="currentColor" />
            </div>
            <div style={styles.emptyTitle}>No comments yet</div>
            <div style={styles.emptyText}>Be the first to comment on this post.</div>
          </div>
        ) : (
          tree.map((comment) => (
            <MediaCommentCard
              key={getCommentId(comment)}
              item={comment}
              postId={postId}
              myChatId={myChatId}
              resolveUserAvatar={resolveUserAvatar}
              replyingCommentId={replyingCommentId}
              setReplyingCommentId={openReply}
              replyText={replyText}
              setReplyText={setReplyText}
              isSubmitting={isSubmitting}
              onSubmitReply={handleSubmitReply}
              autoExpandIds={autoExpandIds}
              onLikeComment={onLikeComment}
              onSupportComment={onSupportComment}
              onViewCommentEarnings={onViewCommentEarnings}
              onTrackCommentView={onTrackCommentView}
              onShareComment={handleShareComment}
              onOpenUser={onOpenUser}
            />
          ))
        )}
      </main>

      {/* COMPOSER — top-level comment on the post */}
      <div style={styles.composer}>
        <div style={styles.composerAvatar}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" style={styles.avatarImage} />
          ) : (
            <span style={styles.avatarFallback}>
              {(currentUser?.name || currentUser?.username || "U")
                .charAt(0)
                .toUpperCase()}
            </span>
          )}
        </div>

        <div style={styles.inputWrapper}>
          <textarea
            id="media-comment-input"
            value={commentText}
            onFocus={closeReply}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitTopLevel();
              }
            }}
            placeholder="Write a comment..."
            rows={1}
            disabled={isSubmitting}
            style={styles.input}
          />

          <button
            type="button"
            onClick={handleSubmitTopLevel}
            disabled={isSubmitting || !commentText.trim()}
            style={{
              ...styles.sendButton,
              opacity: isSubmitting || !commentText.trim() ? 0.45 : 1,
            }}
            aria-label="Send comment"
            title="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const styles = {
  // absolute (not fixed): covers just the Moment screen / centre column,
  // not the whole browser window on desktop.
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1100,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
  },

  // transparent: Moment shrinks the video above the sheet, so the area
  // behind the sheet is just the black Moment background. Still catches
  // taps to close.
  backdrop: {
    position: "absolute",
    inset: 0,
    background: "transparent",
  },

  sheet: {
    position: "relative",
    height: "68%",
    maxHeight: "calc(100% - 60px)",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    color: "#111",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: "hidden",
    boxShadow: "0 -8px 30px rgba(0,0,0,0.25)",
    animation: "mcSheetUp 240ms cubic-bezier(0.22, 1, 0.36, 1)",
  },

  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: "#d4d4d4",
    margin: "8px auto 0",
    flexShrink: 0,
  },

  header: {
    height: 46,
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    borderBottom: "1px solid #ececec",
    background: "#fff",
    flexShrink: 0,
  },

  closeButton: {
    width: 36,
    height: 36,
    border: 0,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#111",
    borderRadius: 18,
  },

  headerTitle: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  },

  headerSpacer: { width: 36 },

  avatar: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#e7e7e7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  composerAvatar: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#e7e7e7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    color: "#555",
  },

  commentList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "10px 12px 18px",
    WebkitOverflowScrolling: "touch",
  },

  commentWrapper: { width: "100%" },
  commentRow: { display: "flex", gap: 9, padding: "8px 0" },
  commentBody: { flex: 1, minWidth: 0 },

  commentBubble: {
    display: "inline-block",
    maxWidth: "100%",
    padding: "8px 11px",
    borderRadius: 14,
    background: "#f2f2f2",
  },

  authorRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 7,
    marginBottom: 2,
    flexWrap: "wrap",
  },

  authorName: { fontWeight: 700, fontSize: 13 },
  username: { fontSize: 12, color: "#777" },

  commentText: {
    fontSize: 14,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  commentMediaWrap: { display: "flex", flexDirection: "column", gap: 6 },

  commentMedia: {
    marginTop: 8,
    maxWidth: 260,
    borderRadius: 10,
    overflow: "hidden",
    background: "#ddd",
  },

  commentMediaImage: {
    display: "block",
    width: "100%",
    maxHeight: 260,
    objectFit: "cover",
  },

  metaRow: { paddingLeft: 5, marginTop: 3 },
  time: { fontSize: 11, color: "#888" },

  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginTop: 2,
    flexWrap: "wrap",
  },

  actionButton: {
    minWidth: 34,
    height: 30,
    padding: "0 5px",
    border: 0,
    background: "transparent",
    color: "#666",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    cursor: "pointer",
    borderRadius: 15,
  },

  viewStat: {
    minWidth: 34,
    height: 30,
    padding: "0 5px",
    color: "#666",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    userSelect: "none",
  },

  activeComment: { color: "#1d9bf0" },
  activeLike: { color: "#e53935" },
  actionCount: { fontSize: 11, lineHeight: 1 },

  replyToggle: {
    border: 0,
    background: "transparent",
    color: "#1d9bf0",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "5px 5px",
  },

  replies: {
    marginLeft: 6,
    marginTop: 3,
    paddingLeft: 10,
    borderLeft: "1px solid #e5e5e5",
  },

  inlineReply: {
    marginTop: 6,
    padding: "8px 10px",
    background: "#f7f7f7",
    border: "1px solid #e7e7e7",
    borderRadius: 12,
  },

  inlineReplyLabel: { fontSize: 12, color: "#666", marginBottom: 6 },

  inlineReplyInput: {
    width: "100%",
    boxSizing: "border-box",
    resize: "none",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 14,
    lineHeight: 1.4,
    fontFamily: "inherit",
    outline: "none",
    background: "#fff",
    color: "#111",
  },

  inlineReplyActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 12,
    marginTop: 8,
  },

  inlineCancel: {
    border: 0,
    background: "transparent",
    color: "#666",
    fontSize: 13,
    cursor: "pointer",
    padding: "6px 4px",
  },

  inlineSend: {
    border: 0,
    borderRadius: 999,
    background: "#1d9bf0",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    padding: "7px 16px",
    cursor: "pointer",
  },

  emptyState: {
    minHeight: 260,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 30,
    color: "#777",
  },

  emptyIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "#f2f2f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
    color: "#777",
  },

  emptyTitle: { fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 5 },
  emptyText: { fontSize: 13 },

  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    padding: "8px 10px",
    paddingBottom:
      "calc(8px + env(safe-area-inset-bottom, 0px) + var(--bottom-tabs-h, 0px))",
    borderTop: "1px solid #e5e5e5",
    background: "#fff",
    flexShrink: 0,
  },

  inputWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    display: "flex",
    alignItems: "flex-end",
    gap: 5,
    border: "1px solid #ddd",
    borderRadius: 21,
    background: "#f7f7f7",
    padding: "5px 6px 5px 13px",
  },

  input: {
    flex: 1,
    minWidth: 0,
    maxHeight: 100,
    resize: "none",
    border: 0,
    outline: "none",
    background: "transparent",
    color: "#111",
    fontSize: 14,
    lineHeight: 1.4,
    padding: "5px 0",
    fontFamily: "inherit",
  },

  sendButton: {
    width: 34,
    height: 34,
    minWidth: 34,
    border: 0,
    borderRadius: "50%",
    background: "#111",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
};

// Named alias so `import { mediaComment } from "./mediaComment"` still works.
export { MediaComment as mediaComment };